import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import Brand from "../components/Brand";
import AffiliateDisclosure from "../components/AffiliateDisclosure";
import {
  ShoppingBag, BookOpen, Pill, Shirt, Sparkles, ArrowRight, ExternalLink,
  Lock, Unlock, GraduationCap, Crown, DollarSign, CheckCircle2, ChevronRight,
} from "lucide-react";

/* /market — the single shop hub.
   Replaces /affilirama, /partners, and the old Books/Supplements separate
   nav items. One place for everything for sale:
     1. BOOKS — Mike's library + member books
     2. SUPPLEMENTS — Goli + Piurify + other vetted partners
     3. INCOME STREAMS — the 5 affiliate programs (locked for free, open for paid)
     4. MERCH — coming-soon placeholder for future Faitharama merchandise
*/

const G = "#2E9F3A";
const O = "#F39A1F";
const WINE = "#0F172A";
const DARK = "#0F172A";
const CREAM = "#FFFFFF";

const SECTIONS = [
  { id: "supplements", label: "Supplements",  Icon: Pill,     color: G },
  { id: "streams",     label: "Income Streams", Icon: DollarSign, color: O },
  { id: "merch",       label: "Merch",        Icon: Shirt,    color: "#3B82F6" },
];

export default function MarketPage({ embedded = false }) {
  const { user } = useAuth();
  const [books, setBooks] = useState([]);
  const [partners, setPartners] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [isPaid, setIsPaid] = useState(false);

  useEffect(() => {
    api.get("/books").then((r) => setBooks(r.data?.books || [])).catch(() => {});
    api.get("/partners").then((r) => setPartners(r.data?.partners || [])).catch(() => {});
    const endpoint = user ? "/me/affiliate-programs" : "/affiliate-programs";
    api.get(endpoint).then((r) => {
      setPrograms(r.data?.programs || []);
      setIsPaid(!!r.data?.is_paid);
    }).catch(() => {});
  }, [user]);

  return (
    <div className="min-h-screen bg-[#EAF6FC]" data-testid="market-page">
      {!embedded && <SiteNav />}

      {/* ═══ HERO — light cream/gold ═══ */}
      <section className="relative overflow-hidden border-b-2"
               style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 60%, #FFFFFF 100%)",
                        borderColor: "#2E9F3A" }}>
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 pointer-events-none"
             style={{ background: "radial-gradient(circle, #2E9F3A 0%, transparent 70%)" }} />
        <div className="relative max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] tracking-[0.28em] uppercase font-bold mb-4"
               style={{ background: "#EAF6FC", color: "#FFFFFF" }}>
            <ShoppingBag className="w-3.5 h-3.5" /> ★ The Market
          </div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.95] uppercase"
              style={{ textWrap: "balance", color: "#0F172A" }}>
            <span style={{ color: "#2E9F3A" }}>One market.</span><br />
            Everything we recommend.
          </h1>
          <p className="text-base md:text-lg italic mt-5 max-w-2xl"
             style={{ textWrap: "pretty", color: "#0F172A" }}>
            Books, supplements, income streams, and merch — pick one or all of the products we recommend. <strong style={{ color: "#2E9F3A" }}>Two ways to earn:</strong> use our affiliate approval and keep 50% of what <Brand /> collects, OR apply direct with the brand and keep whatever commission they pay you. Your choice, every product.
          </p>

          {/* Section chips — jump links */}
          <div className="mt-7 flex flex-wrap gap-2.5" data-testid="market-chip-row">
            {SECTIONS.map(({ id, label, Icon, color }) => (
              <a key={id} href={`#${id}`}
                 data-testid={`market-chip-${id}`}
                 className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-[0.18em] font-bold border-2 transition hover:scale-[1.03] shadow-sm"
                 style={{ background: color, borderColor: color, color: id === "merch" ? "white" : DARK }}>
                <Icon className="w-3.5 h-3.5" /> {label}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ BOOKS section removed — Mike going with Bookstera affiliate instead ═══ */}

      {/* ═══ 2. SUPPLEMENTS ═══ */}
      <SectionHeader id="supplements" eyebrow="Faith & Wellness" title="Supplements & Wellness" sub="Vetted brands. Plant-based. Clean ingredients. Mike uses every one." Icon={Pill} color={G} />
      <AffiliateDisclosure />

      {/* ── Mike's Personal-Vet Promise (the trust wedge) ── */}
      <div className="max-w-6xl mx-auto px-5 md:px-8 pb-6">
        <div className="rounded-2xl border-4 px-5 md:px-7 py-5 md:py-6 shadow-md"
             style={{ borderColor: G, background: "linear-gradient(180deg,#F0FAF1 0%,#E0F2FE 100%)" }}
             data-testid="mike-vetted-promise">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-white shadow"
                 style={{ background: G }}>
              <Pill className="w-6 h-6"/>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: "#C9202E" }}>
                ★ FAITHARAMA.COM PROMISE
              </div>
              <h3 className="font-serif-display text-xl md:text-2xl uppercase mt-1" style={{ color: "#0F172A" }}>
                Mike Personally Vets Every Product on This Page
              </h3>
              <p className="text-sm md:text-base mt-2 leading-relaxed" style={{ color: "#0F172A" }}>
                Every supplement, every brand, every partner listed below has been used by Mike or someone Mike trusts. If it&apos;s not on this page, it didn&apos;t make the cut. <strong>You won&apos;t find these vetted on any other wellness platform.</strong>
              </p>
            </div>
          </div>
        </div>
      </div>

      <section className="max-w-6xl mx-auto px-5 md:px-8 pb-10 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="market-supplements">

        {/* GOLI — real customer photo */}
        <Link to="/goli" data-testid="market-supp-goli"
              className="group rounded-2xl overflow-hidden border-2 shadow-md hover:shadow-2xl hover:-translate-y-1 transition bg-white block"
              style={{ borderColor: "rgba(46,159,58,0.4)" }}>
          <div className="relative aspect-[5/4] overflow-hidden bg-white">
            <img src="https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/gs6eb9by_Screenshot%202026-05-13%20221254.png"
                 alt="Real Goli customer smiling, holding her bottle of apple-cider gummies"
                 className="block w-full h-full object-contain p-3 group-hover:scale-[1.04] transition-transform duration-500" loading="lazy"/>
            <span className="absolute top-3 left-3 text-[10px] tracking-[0.22em] uppercase font-bold px-2.5 py-1.5 rounded-full text-white shadow-md"
                  style={{ background: G }}>★ MIKE'S DAILY PICK</span>
          </div>
          <div className="p-5">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: G }}>Apple Cider Vinegar Gummies</div>
            <div className="font-serif-display text-3xl mt-1" style={{ color: WINE, fontWeight: 700 }}>Goli</div>
            <p className="text-base text-foreground/85 mt-2 leading-relaxed">Plant-based gummies. Free shipping to 200+ countries. Earn <strong>20% commission</strong> on every sale.</p>
            <div className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow group-hover:scale-[1.02] transition" style={{ background: G }}>
              See Goli Page <ArrowRight className="w-4 h-4"/>
            </div>
          </div>
        </Link>

        {/* BRAINZYME — REMOVED Feb 2026 (Mike's call). */}

        {/* PIURIFY — clean product photo (rose hero bottle).
            Previous URL was a marketing infographic that cropped to show
            text bits ("DD TO CART", "Innovation That Powers Your He") —
            unreadable inside the 5/4 aspect ratio card. */}
        <Link to="/piurify" data-testid="market-supp-piurify"
              className="group rounded-2xl overflow-hidden border-2 shadow-md hover:shadow-2xl hover:-translate-y-1 transition bg-white block"
              style={{ borderColor: "rgba(40,127,142,0.45)" }}>
          <div className="relative aspect-[5/4] overflow-hidden flex items-center justify-center"
               style={{ background: "linear-gradient(180deg, #E8F2F5 0%, #C9DDE3 100%)" }}>
            <img src="https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/xg6blyqf_Screenshot%202026-05-13%20134456.png"
                 alt="Piurify portable hydrogen water bottle in rose pink — clean product photo"
                 className="block max-h-[92%] w-auto object-contain group-hover:scale-[1.04] transition-transform duration-500 drop-shadow-2xl" loading="lazy"/>
            <span className="absolute top-3 left-3 text-[10px] tracking-[0.22em] uppercase font-bold px-2.5 py-1.5 rounded-full text-white shadow-md"
                  style={{ background: "#287F8E" }}>★ HEALTH UPGRADE</span>
          </div>
          <div className="p-5">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: "#287F8E" }}>Hydrogen Water Bottle</div>
            <div className="font-serif-display text-3xl mt-1" style={{ color: WINE, fontWeight: 700 }}>Piurify</div>
            <p className="text-base text-foreground/85 mt-2 leading-relaxed">Portable hydrogen-water generator. <strong>—</strong> · 60-day returns · lifetime warranty · free shipping. Mike earns 10% per sale.</p>
            <div className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow group-hover:scale-[1.02] transition" style={{ background: "#287F8E" }}>
              See Piurify Page <ArrowRight className="w-4 h-4"/>
            </div>
          </div>
        </Link>

        {/* NATURE'S BRANDS — REMOVED Feb 2026 (Mike rotating Christian-aligned organic partners in). */}

        {/* BEE&YOU — Refersion affiliate, raw honey + propolis + royal jelly. Added Feb 2026. */}
        <a href="https://www.beeandyou.com/?rfsn=9150485.60554a&utm_source=refersion&utm_medium=affiliate&utm_campaign=9150485.60554a"
           target="_blank" rel="noopener sponsored"
           data-testid="market-supp-bee-and-you"
           className="group rounded-2xl overflow-hidden border-2 shadow-md hover:shadow-2xl hover:-translate-y-1 transition bg-white block"
           style={{ borderColor: "rgba(243,154,31,0.5)" }}>
          <div className="relative aspect-[5/4] overflow-hidden bg-white flex items-center justify-center">
            <img src="https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/o08i64t2_Screenshot%202026-06-06%20125500.png"
                 alt="Bee&You bee products — Royal Jelly + Bee Pollen + Propolis capsules, Propolis Raw Honey jar, Propolis Throat Spray bottle and box on a marble counter"
                 className="block w-full h-full object-contain p-3 group-hover:scale-[1.04] transition-transform duration-500" loading="lazy"/>
            <span className="absolute top-3 left-3 text-[10px] tracking-[0.22em] uppercase font-bold px-2.5 py-1.5 rounded-full text-white shadow-md"
                  style={{ background: "#F39A1F" }}>★ NEW · IMMUNITY</span>
          </div>
          <div className="p-5">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: "#F39A1F" }}>Bee Products · Immunity Connection</div>
            <div className="font-serif-display text-3xl mt-1" style={{ color: WINE, fontWeight: 700 }}>Bee&You</div>
            <p className="text-base text-foreground/85 mt-2 leading-relaxed">Raw honey, royal jelly, bee pollen, propolis. <strong>Nature's powerful ingredients</strong> that help support your body's natural defenses. From the Anatolia land.</p>
            <div className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow group-hover:scale-[1.02] transition" style={{ background: "#F39A1F" }}>
              Visit Bee&You Market <ArrowRight className="w-4 h-4"/>
            </div>
          </div>
        </a>

        {/* GOGREEN HEMP — Awin affiliate, CBD broad-spectrum tinctures. Added Feb 2026. */}
        <a href="https://www.awin1.com/cread.php?awinmid=88879&awinaffid=2924149"
           target="_blank" rel="noopener sponsored"
           data-testid="market-supp-gogreen-hemp"
           className="group rounded-2xl overflow-hidden border-2 shadow-md hover:shadow-2xl hover:-translate-y-1 transition bg-white block"
           style={{ borderColor: "rgba(46,159,58,0.4)" }}>
          <div className="relative aspect-[5/4] overflow-hidden bg-white flex items-center justify-center">
            <img src="https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/dhv5be2b_Screenshot%202026-06-06%20124437.png"
                 alt="GoGreen Hemp CBD broad-spectrum tinctures in orange, peppermint, and unflavored — 250mg, 500mg, 1000mg bottles with boxes"
                 className="block w-full h-full object-contain p-3 group-hover:scale-[1.04] transition-transform duration-500" loading="lazy"/>
            <span className="absolute top-3 left-3 text-[10px] tracking-[0.22em] uppercase font-bold px-2.5 py-1.5 rounded-full text-white shadow-md"
                  style={{ background: G }}>★ NEW · CBD</span>
          </div>
          <div className="p-5">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: G }}>Broad-Spectrum CBD Tinctures</div>
            <div className="font-serif-display text-3xl mt-1" style={{ color: WINE, fontWeight: 700 }}>GoGreen Hemp</div>
            <p className="text-base text-foreground/85 mt-2 leading-relaxed">Which CBD product is right for you? <strong>Orange · Peppermint · Unflavored</strong> — 250mg / 500mg / 1000mg. 30 servings per bottle. Gluten-free. Lab tested.</p>
            <div className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow group-hover:scale-[1.02] transition" style={{ background: G }}>
              Visit GoGreen Hemp Market <ArrowRight className="w-4 h-4"/>
            </div>
          </div>
        </a>

        {/* POOFY ORGANICS — USDA Certified Organic, 15% + bonuses (faitharama affiliate) */}
        <Link to="/poofy" data-testid="market-supp-poofy"
              className="group rounded-2xl overflow-hidden border-2 shadow-md hover:shadow-2xl hover:-translate-y-1 transition bg-white block"
              style={{ borderColor: "rgba(46,125,50,0.55)" }}>
          <div className="relative aspect-[5/4] overflow-hidden bg-white flex items-center justify-center">
            <img src="/affiliates/poofy-products.png"
                 alt="Poofy Organics — USDA Certified Organic, toxin-free product lineup"
                 className="block w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" loading="lazy"/>
            <span className="absolute top-3 left-3 text-[10px] tracking-[0.22em] uppercase font-bold px-2.5 py-1.5 rounded-full text-white shadow-md"
                  style={{ background: "#2E7D32" }}>★ USDA CERTIFIED ORGANIC</span>
          </div>
          <div className="p-5">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: "#2E7D32" }}>Toxin-Free Skincare · Body · Baby · Home</div>
            <div className="font-serif-display text-3xl mt-1" style={{ color: WINE, fontWeight: 700 }}>Poofy Organics</div>
            <p className="text-base text-foreground/85 mt-2 leading-relaxed">Real organic ingredients, made in the USA. Members earn <strong>15% on every sale + monthly cash bonuses up to $550.</strong> Lifetime customer credit.</p>
            <div className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow group-hover:scale-[1.02] transition" style={{ background: "#2E7D32" }}>
              See Poofy Page <ArrowRight className="w-4 h-4"/>
            </div>
          </div>
        </Link>

        {/* HOME DOCTOR — Practical Medicine For Every Household (Digistore24 affiliate) */}
        <a href="https://homedoctorbook.com/book/#aff=dealaramas" target="_blank" rel="noopener noreferrer" data-testid="market-supp-home-doctor"
           className="group rounded-2xl overflow-hidden border-2 shadow-md hover:shadow-2xl hover:-translate-y-1 transition bg-white block"
           style={{ borderColor: "rgba(197,17,29,0.45)" }}>
          <div className="relative aspect-[5/4] overflow-hidden bg-white flex items-center justify-center">
            <img src="https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/bwtnt7uv_Screenshot%202026-05-26%20154337.png"
                 alt="The Home Doctor — Practical Medicine For Every Household — by Dr. Maybell Nieves, Dr. Rodrigo Alterio, Claude Davis"
                 className="block max-h-[94%] w-auto object-contain group-hover:scale-[1.04] transition-transform duration-500 drop-shadow-xl" loading="lazy"/>
            <span className="absolute top-3 left-3 text-[10px] tracking-[0.22em] uppercase font-bold px-2.5 py-1.5 rounded-full text-white shadow-md"
                  style={{ background: "#C5111D" }}>★ FAMILY HEALTH BOOK</span>
          </div>
          <div className="p-5">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: "#C5111D" }}>Practical Medicine For Every Household</div>
            <div className="font-serif-display text-3xl mt-1" style={{ color: WINE, fontWeight: 700 }}>The Home Doctor</div>
            <p className="text-base text-foreground/85 mt-2 leading-relaxed">Written by 3 doctors. The 304-page survival medicine guide for when help isn't on the way. <strong>Physical book + bonuses.</strong> Members earn commission on every sale.</p>
            <div className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow group-hover:scale-[1.02] transition" style={{ background: "#C5111D" }}>
              Get The Book <ExternalLink className="w-4 h-4"/>
            </div>
          </div>
        </a>

        {/* SMOOTHIE DIET — 21-Day Smoothie Diet (Digistore24 affiliate) */}
        <a href="https://official.smoothiediet.com/#aff=dealaramas" target="_blank" rel="noopener noreferrer" data-testid="market-supp-smoothie-diet"
           className="group rounded-2xl overflow-hidden border-2 shadow-md hover:shadow-2xl hover:-translate-y-1 transition bg-white block"
           style={{ borderColor: "rgba(123,194,76,0.55)" }}>
          <div className="relative aspect-[5/4] overflow-hidden flex items-center justify-center"
               style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 100%)" }}>
            <img src="https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/yn1tyvgr_Screenshot%202026-05-26%20160531.png"
                 alt="The Smoothie Diet 21-day weight-loss program — Quick Start Guide, Main eBook, and 3-Day Detox"
                 className="block max-h-[94%] w-auto object-contain group-hover:scale-[1.04] transition-transform duration-500 drop-shadow-xl" loading="lazy"/>
            <span className="absolute top-3 left-3 text-[10px] tracking-[0.22em] uppercase font-bold px-2.5 py-1.5 rounded-full text-white shadow-md"
                  style={{ background: "#7BC24C" }}>★ INSTANT DOWNLOAD</span>
          </div>
          <div className="p-5">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: "#5A9637" }}>21-Day Weight Loss Program</div>
            <div className="font-serif-display text-3xl mt-1" style={{ color: WINE, fontWeight: 700 }}>The Smoothie Diet</div>
            <p className="text-base text-foreground/85 mt-2 leading-relaxed">Lose weight, feel amazing. Quick-start guide + main eBook + 3-day detox. <strong>$27 instant download · 60-day guarantee.</strong> Members earn commission on every sale.</p>
            <div className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow group-hover:scale-[1.02] transition" style={{ background: "#7BC24C" }}>
              Get The Program <ExternalLink className="w-4 h-4"/>
            </div>
          </div>
        </a>

        {/* REFLUX ONLINE SUMMIT 2026 — Digistore24 affiliate (a commission per Summit Package) */}
        <a href="https://www.checkout-ds24.com/redir/593656/dealaramas/" target="_blank" rel="noopener noreferrer" data-testid="market-supp-reflux-summit"
           className="group rounded-2xl overflow-hidden border-2 shadow-md hover:shadow-2xl hover:-translate-y-1 transition bg-white block"
           style={{ borderColor: "rgba(30,107,184,0.55)" }}>
          <div className="relative aspect-[5/4] overflow-hidden flex items-center justify-center"
               style={{ background: "#1E6BB8" }}>
            <img src="https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/dqggyq74_Screenshot%202026-05-26%20161425.png"
                 alt="Reflux Online Summit 2026 — 30+ experts explain causes and solutions to acid reflux — May 25-31, 2026"
                 className="block max-h-[96%] w-auto object-contain group-hover:scale-[1.04] transition-transform duration-500 drop-shadow-xl" loading="lazy"/>
            <span className="absolute top-3 left-3 text-[10px] tracking-[0.22em] uppercase font-bold px-2.5 py-1.5 rounded-full text-white shadow-md"
                  style={{ background: "#0F4A82" }}>★ EARN a commission / SALE</span>
          </div>
          <div className="p-5">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: "#1E6BB8" }}>Free Online Health Summit · May 25–31, 2026</div>
            <div className="font-serif-display text-3xl mt-1" style={{ color: WINE, fontWeight: 700 }}>Reflux Online Summit 2026</div>
            <p className="text-base text-foreground/85 mt-2 leading-relaxed">30+ doctors, researchers, and practitioners explain what causes acid reflux and how to resolve it. Free to attend. <strong>Members earn a commission per Summit Package sale.</strong></p>
            <div className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow group-hover:scale-[1.02] transition" style={{ background: "#1E6BB8" }}>
              Start Free Trial <ExternalLink className="w-4 h-4"/>
            </div>
          </div>
        </a>

        {/* PROSTAVIVE — Prostate supplement, Digistore24/ClickBank affiliate, 75–80% commissions */}
        <a href="https://hop.clickbank.net/?affiliate=faitharama&vendor=provive&pid=20&tid=track" target="_blank" rel="noopener noreferrer" data-testid="market-supp-prostavive"
           className="group rounded-2xl overflow-hidden border-2 shadow-md hover:shadow-2xl hover:-translate-y-1 transition bg-white block"
           style={{ borderColor: "rgba(48,116,255,0.55)" }}>
          <div className="relative aspect-[5/4] overflow-hidden flex items-center justify-center bg-white">
            <img src="/partners/prostavive.png"
                 alt="ProstaVive — Advanced prostate health formula · 75-80% affiliate commission"
                 className="block max-h-[94%] w-auto object-contain group-hover:scale-[1.04] transition-transform duration-500 drop-shadow-xl" loading="lazy"/>
            <span className="absolute top-3 left-3 text-[10px] tracking-[0.22em] uppercase font-bold px-2.5 py-1.5 rounded-full text-white shadow-md"
                  style={{ background: "#3074FF" }}>★ EARN 75–80%</span>
          </div>
          <div className="p-5">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: "#3074FF" }}>Advanced Prostate Health Formula</div>
            <div className="font-serif-display text-3xl mt-1" style={{ color: WINE, fontWeight: 700 }}>ProstaVive</div>
            <p className="text-base text-foreground/85 mt-2 leading-relaxed">Supports healthy prostate size & function. — average order value · 180-day guarantee · low refunds. <strong>Members earn 75–80% commission on every sale.</strong></p>
            <div className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow group-hover:scale-[1.02] transition" style={{ background: "#3074FF" }}>
              Get The Bottle <ExternalLink className="w-4 h-4"/>
            </div>
          </div>
        </a>

        {/* JOINTGEN — Joint health (ClickBank affiliate via faitharama vendor) */}
        <a href="https://hop.clickbank.net/?affiliate=faitharama&vendor=jointgen&v=bvsl" target="_blank" rel="noopener noreferrer" data-testid="market-supp-jointgen"
           className="group rounded-2xl overflow-hidden border-2 shadow-md hover:shadow-2xl hover:-translate-y-1 transition bg-white block"
           style={{ borderColor: "rgba(167,123,52,0.55)" }}>
          <div className="relative aspect-[5/4] overflow-hidden flex items-center justify-center bg-black">
            <img src="/partners/jointgen.png"
                 alt="JointGen — The remote Japanese village where joint decay doesn't exist"
                 className="block w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500 drop-shadow-xl" loading="lazy"/>
            <span className="absolute top-3 left-3 text-[10px] tracking-[0.22em] uppercase font-bold px-2.5 py-1.5 rounded-full text-white shadow-md"
                  style={{ background: "#A77B34" }}>★ HIGH PAYOUT</span>
          </div>
          <div className="p-5">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: "#A77B34" }}>Natural Joint Health Formula</div>
            <div className="font-serif-display text-3xl mt-1" style={{ color: WINE, fontWeight: 700 }}>JointGen</div>
            <p className="text-base text-foreground/85 mt-2 leading-relaxed">Ancient Japanese formula for healthy joint cartilage and pain-free movement. Strong VSL, high conversion, low refunds. <strong>Members earn affiliate commission on every sale.</strong></p>
            <div className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow group-hover:scale-[1.02] transition" style={{ background: "#A77B34" }}>
              Watch The Video <ExternalLink className="w-4 h-4"/>
            </div>
          </div>
        </a>

        {/* SIMPLE SLIM KITCHEN — 30-day meal-planning ebook (ClickBank hop link) */}
        <a href="https://956c03oe2cqqd7n9wj1oc0freg.hop.clickbank.net" target="_blank" rel="noopener noreferrer" data-testid="market-supp-simple-slim-kitchen"
           className="group rounded-2xl overflow-hidden border-2 shadow-md hover:shadow-2xl hover:-translate-y-1 transition bg-white block"
           style={{ borderColor: "rgba(57,127,68,0.55)" }}>
          <div className="relative aspect-[5/4] overflow-hidden flex items-center justify-center bg-white">
            <img src="/partners/simple-slim-kitchen.png"
                 alt="The Simple Slim Kitchen — 30-day recipe and meal-planning system, 62 recipes"
                 className="block w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500 drop-shadow-xl" loading="lazy"/>
            <span className="absolute top-3 left-3 text-[10px] tracking-[0.22em] uppercase font-bold px-2.5 py-1.5 rounded-full text-white shadow-md"
                  style={{ background: "#397F44" }}>★ MEAL PLAN</span>
          </div>
          <div className="p-5">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: "#397F44" }}>30-Day Recipe & Meal-Planning System</div>
            <div className="font-serif-display text-3xl mt-1" style={{ color: WINE, fontWeight: 700 }}>The Simple Slim Kitchen</div>
            <p className="text-base text-foreground/85 mt-2 leading-relaxed">62 simple, delicious recipes. 30-day meal plan. Weekly shopping lists. Meal-prep routines. Trackers & checklists. <strong>Members earn commission on every sale.</strong></p>
            <div className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow group-hover:scale-[1.02] transition" style={{ background: "#397F44" }}>
              Get The Book <ExternalLink className="w-4 h-4"/>
            </div>
          </div>
        </a>

        {/* RC RACERS — new affiliate partner (race-track black + electric yellow) */}
        <Link to="/rc-racers" data-testid="market-supp-rc-racers"
              className="group rounded-2xl overflow-hidden border-2 shadow-md hover:shadow-2xl hover:-translate-y-1 transition bg-white block"
              style={{ borderColor: "rgba(212,255,0,0.6)" }}>
          <div className="relative aspect-[5/4] overflow-hidden"
               style={{ background: "#0F1116" }}>
            <img src="https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/1pxqz8cn_Screenshot%202026-05-17%20123913.png"
                 alt="RC Racers — Team Losi TLR 22X buggies in Dirt/Clay and Astro/Carpet trims"
                 className="block w-full h-full object-contain p-3 group-hover:scale-[1.04] transition-transform duration-500" loading="lazy"/>
            <span className="absolute top-3 left-3 text-[10px] tracking-[0.22em] uppercase font-bold px-2.5 py-1.5 rounded-full shadow-md"
                  style={{ background: "#D4FF00", color: "#0F1116" }}>★ NEW PARTNER</span>
          </div>
          <div className="p-5">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: "#7A8A00" }}>Remote Control Racing · BeachRC</div>
            <div className="font-serif-display text-3xl mt-1" style={{ color: WINE, fontWeight: 700 }}>RC Racers</div>
            <p className="text-base text-foreground/85 mt-2 leading-relaxed">Team Losi race buggies, scale crawlers, Traxxas bashers, street/drag rigs, and team apparel — through Mike's BeachRC partnership. <strong>The best father-son hobby on earth</strong> — and you earn on every rig.</p>
            <div className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-bold uppercase tracking-[0.18em] shadow group-hover:scale-[1.02] transition"
                 style={{ background: "#0F1116", color: "#D4FF00" }}>
              See RC Racers Page <ArrowRight className="w-4 h-4"/>
            </div>
          </div>
        </Link>

        {/* LIVEGOOD — REMOVED Feb 2026 (Mike pulled — MLM concerns). */}

        {/* Vitalura Labs removed (Mike's call, May 2026) */}
      </section>

      {/* Additional partners (from /api/partners) shown as a compact strip below */}
      {partners.filter((p) => !["goli", "piurify"].includes(p.id)).length > 0 && (
        <section className="max-w-6xl mx-auto px-5 md:px-8 pb-12">
          <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-3" style={{ color: G }}>★ More Vetted Brands</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {partners.filter((p) => !["goli", "piurify"].includes(p.id)).map((p) => (
              <a key={p.id} href={p.signup_url || p.shop_url || "#"} target="_blank" rel="noopener noreferrer"
                 data-testid={`market-partner-${p.id}`}
                 className="rounded-xl bg-white border-2 border-border hover:border-[#F39A1F] hover:shadow-lg p-3 transition flex flex-col items-center text-center">
                {p.logo_url ? <img src={p.logo_url} alt={p.name} className="h-10 w-auto object-contain mb-2" loading="lazy" /> : <Pill className="w-8 h-8 mb-2" style={{ color: G }} />}
                <div className="text-xs font-bold text-foreground leading-tight">{p.name}</div>
                <div className="text-[10px] text-foreground/60 mt-0.5 leading-snug">{p.commission_label}</div>
              </a>
            ))}
          </div>
        </section>
      )}

      {/* ═══ 3. INCOME STREAMS (from old /affilirama) ═══ */}
      <SectionHeader id="streams" eyebrow="Earn With Us" title="Income Streams" sub="Five programs Mike vetted personally. Real commissions on what you actually believe in." Icon={DollarSign} color={O} />
      <section className="max-w-6xl mx-auto px-5 md:px-8 pb-6" data-testid="market-streams">
        {/* Commission ladder — TWO WAYS TO EARN (Mike's directive Feb 2026) */}
        <div className="rounded-2xl border-4 p-6 md:p-7 mb-7 shadow-md"
             style={{ background: "linear-gradient(135deg, #FFFFFF 0%, #EAF6FC 100%)", borderColor: "#2E9F3A" }}>
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: "#2E9F3A" }}>
            ★ TWO WAYS TO EARN
          </div>
          <h3 className="font-serif-display text-2xl md:text-3xl uppercase leading-tight mt-1" style={{ color: "#0F172A", textWrap: "balance" }}>
            Visit Our Market — Pick One Or All Of The Products We Recommend.
          </h3>

          <div className="grid md:grid-cols-2 gap-4 mt-5">
            {/* OPTION 1 */}
            <div className="rounded-xl p-5 border-2" style={{ background: "#FFFFFF", borderColor: "#2E9F3A" }}>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ background: "#2E9F3A" }}>1</span>
                <div className="text-[11px] tracking-[0.24em] uppercase font-bold" style={{ color: "#2E9F3A" }}>OPTION 1 · EASIEST</div>
              </div>
              <h4 className="font-bold text-base md:text-lg" style={{ color: "#0F172A" }}>Use Our Affiliate Approval</h4>
              <p className="text-sm mt-2 leading-relaxed" style={{ color: "#0F172A" }}>
                Just share your <Brand suffix=".com"/> link. We split what we earn with you. You keep <strong style={{ color: "#2E9F3A" }}>50% of every dollar Faitharama collects</strong> on the sale.
              </p>
              <ul className="mt-3 space-y-1.5 text-xs" style={{ color: "#3A2A1F" }}>
                <li>✓ No applications</li>
                <li>✓ No waiting · No rejections</li>
                <li>✓ Works on day one</li>
              </ul>
            </div>

            {/* OPTION 2 */}
            <div className="rounded-xl p-5 border-2" style={{ background: "#FFFFFF", borderColor: "#F39A1F" }}>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ background: "#F39A1F" }}>2</span>
                <div className="text-[11px] tracking-[0.24em] uppercase font-bold" style={{ color: "#F39A1F" }}>OPTION 2 · MORE WORK · BIGGER CHECK</div>
              </div>
              <h4 className="font-bold text-base md:text-lg" style={{ color: "#0F172A" }}>Apply Direct With The Brand</h4>
              <p className="text-sm mt-2 leading-relaxed" style={{ color: "#0F172A" }}>
                Apply to the brand yourself (Goli, Piurify, Vanotium, etc). Use <strong>YOUR OWN</strong> affiliate link instead of ours and keep <strong style={{ color: "#F39A1F" }}>whatever percentage they pay you</strong>.
              </p>
              <ul className="mt-3 space-y-1.5 text-xs" style={{ color: "#3A2A1F" }}>
                <li>✓ Most brands pay 10–20% to new affiliates</li>
                <li>✓ Their full payout — straight to you</li>
                <li>✓ Requires you to apply &amp; get approved</li>
              </ul>
            </div>
          </div>

          <p className="mt-5 text-sm text-center italic" style={{ color: "#3A2A1F" }}>
            ★ You pick which option per product. Mix and match. <Brand /> walks you through it in the video tutorial.
          </p>
        </div>

        {/* The 5 programs */}
        {programs.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {programs.map((p) => (
              <ProgramCard key={p.id} program={p} isPaid={isPaid} />
            ))}
          </div>
        )}

        {!isPaid && (
          <div className="text-center mt-7">
            <Link to="/signup" data-testid="market-streams-upgrade"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow-lg transition hover:opacity-90"
                  style={{ background: G }}>
              <Unlock className="w-4 h-4" /> Unlock All 5 · $49/mo
            </Link>
          </div>
        )}
      </section>

      {/* COACHING CERT CALL-OUT — flagship bundle */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 pb-14">
        <Link to="/signup?plan=coaching_cert" data-testid="market-coaching-cert"
              className="block rounded-2xl overflow-hidden border-4 border-[#F39A1F] bg-white hover:shadow-2xl hover:-translate-y-1 transition group">
          <div className="grid md:grid-cols-[1fr_420px]">
            <div className="p-6 md:p-10">
              <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: O }}>★ FAST START PLAYBOOK</div>
              <h3 className="font-serif-display text-3xl md:text-4xl uppercase leading-[1.05] mt-2" style={{ color: WINE, textWrap: "balance" }}>
                $1,000 A Month In 90 Days. <span style={{ color: O }}>Plain English.</span>
              </h3>
              <p className="text-sm text-foreground/85 mt-3 leading-relaxed">
                Our exact 90-day playbook for folks who want to start earning fast. <strong>Paperback book + 30-day digital challenge + 5 video modules.</strong>
              </p>
              <div className="mt-3 rounded-lg px-4 py-3" style={{ background: "#EAF6FC", border: `2px solid #2E9F3A` }}>
                <div className="text-[10px] tracking-[0.28em] uppercase font-bold mb-1" style={{ color: "#2E9F3A" }}>★ A NOTE FROM <Brand /></div>
                <p className="text-sm text-foreground leading-relaxed">
                  <strong>This is your best first investment.</strong> Step-by-step instructions to hit <strong style={{ color: WINE }}>$1,000/month in 90 days</strong> using only your <Brand /> tools and the Fast Start playbook.
                </p>
              </div>
              <div className="text-3xl font-serif-display mt-4" style={{ color: WINE }}>$27 <span className="text-sm uppercase tracking-[0.2em] font-bold align-middle ml-1 text-foreground/55">one-time · yours forever</span></div>
              <div className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow group-hover:opacity-90 transition" style={{ background: "#2E9F3A" }}>
                <GraduationCap className="w-4 h-4" /> Start Earning In 90 Days <ChevronRight className="w-4 h-4" />
              </div>
              <div className="text-[11px] italic text-foreground/55 mt-2">One-time · Lifetime access · Founding price $27.</div>
            </div>
            {/* Real bundle product image */}
            <div className="hidden md:flex items-center justify-center p-5"
                 style={{ background: "linear-gradient(135deg, #FFFFFF 0%, #EAF6FC 100%)" }}>
              <img src="/free-guide/fast-start-bundle.png"
                   alt="Fast Start Guide — $1,000/month in 90 days · $7.95"
                   loading="lazy"
                   className="block w-full h-auto rounded-md shadow-lg"
                   style={{ maxWidth: 380 }} />
            </div>
          </div>
        </Link>
      </section>

      {/* ═══ 4. MERCH (placeholder) ═══ */}
      <SectionHeader id="merch" eyebrow="Soon" title="Merch" sub="Faitharama tees, mugs, and accessories — coming Q3." Icon={Shirt} color="#3B82F6" />
      <section className="max-w-6xl mx-auto px-5 md:px-8 pb-20">
        <div className="rounded-2xl p-10 md:p-14 text-center border-2 border-dashed" style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 100%)", borderColor: "rgba(59,130,246,0.4)" }}>
          <Shirt className="w-14 h-14 mx-auto mb-3" strokeWidth={1.5} style={{ color: "#3B82F6" }} />
          <div className="text-[11px] tracking-[0.28em] uppercase font-bold mb-1" style={{ color: "#3B82F6" }}>★ Coming Soon</div>
          <h3 className="font-serif-display text-3xl md:text-4xl uppercase" style={{ color: WINE }}><Brand /> Merch Store</h3>
          <p className="text-sm text-foreground/70 max-w-xl mx-auto mt-3">
            Tees, mugs, prayer journals, and stickers — printed on demand, no inventory, you keep <strong>100%</strong> of resales from your own page.
          </p>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold mt-5 text-foreground/55">
            Get notified when it launches — already on every member's dashboard.
          </div>
        </div>
      </section>

      {!embedded && <SiteFooter />}
    </div>
  );
}

/* ───────── helpers ───────── */
function SectionHeader({ id, eyebrow, title, sub, Icon, color }) {
  return (
    <section id={id} className="max-w-6xl mx-auto px-5 md:px-8 pt-12 pb-6">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-full flex items-center justify-center shrink-0 shadow-md" style={{ background: color }}>
          <Icon className="w-7 h-7 text-white" strokeWidth={1.75} />
        </div>
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color }}>★ {eyebrow}</div>
          <h2 className="font-serif-display text-3xl md:text-5xl uppercase leading-tight mt-0.5" style={{ color: WINE, textWrap: "balance" }}>{title}</h2>
          <p className="text-sm md:text-base text-foreground/75 max-w-2xl mt-1">{sub}</p>
        </div>
      </div>
    </section>
  );
}

function EmptyState({ label }) {
  return (
    <div className="rounded-xl bg-white border-2 border-dashed border-border p-10 text-center text-foreground/60 italic" style={{ background: "rgba(250,246,232,0.4)" }}>
      {label}
    </div>
  );
}

function ProgramCard({ program, isPaid }) {
  const unlocked = isPaid && !!program.affiliate_url;
  const setupSoon = !program.affiliate_url;
  return (
    <div data-testid={`market-stream-${program.id}`}
         className="group rounded-2xl bg-white border-2 border-border hover:border-[#F39A1F] shadow-sm hover:shadow-2xl transition overflow-hidden flex flex-col">
      <div className="relative aspect-[16/9] bg-white overflow-hidden">
        {program.image_url ? (
          <img src={program.image_url} alt={program.name} loading="lazy"
               className={`w-full h-full object-contain p-3 transition ${!unlocked ? "saturate-50" : "group-hover:scale-105"}`} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-foreground/30"><Sparkles className="w-10 h-10" /></div>
        )}
        {!unlocked && (
          <div className="absolute inset-0 bg-black/45 flex items-center justify-center">
            <div className="text-center text-white">
              <Lock className="w-8 h-8 mx-auto" />
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold mt-1">{setupSoon ? "Coming Soon" : "Members Only"}</div>
            </div>
          </div>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="text-[10px] tracking-[0.18em] uppercase font-bold" style={{ color: O }}>{program.category}</div>
        <div className="font-serif-display text-lg leading-tight mt-0.5" style={{ color: WINE }}>{program.name}</div>
        {program.description && <p className="text-xs text-foreground/75 mt-1.5 leading-relaxed flex-1">{program.description}</p>}
        <div className="grid grid-cols-2 gap-2 mt-3 text-[11px]">
          <div className="rounded p-1.5 border" style={{ background: "#EAF6FC", borderColor: "rgba(243,154,31,0.4)" }}>
            <div className="text-[8px] tracking-[0.18em] uppercase font-bold" style={{ color: WINE }}>Commission</div>
            <div className="font-bold" style={{ color: WINE }}>{program.commission_text || "—"}</div>
          </div>
          <div className="rounded p-1.5 border" style={{ background: "#E8F5DD", borderColor: "rgba(46,159,58,0.4)" }}>
            <div className="text-[8px] tracking-[0.18em] uppercase font-bold" style={{ color: G }}>Est. /mo</div>
            <div className="font-bold" style={{ color: DARK }}>{program.est_monthly_income || "—"}</div>
          </div>
        </div>
        {unlocked ? (
          <a href={program.affiliate_url} target="_blank" rel="noopener noreferrer"
             data-testid={`market-stream-open-${program.id}`}
             className="mt-3 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded text-[11px] font-bold uppercase tracking-[0.18em] text-white" style={{ background: G }}>
            <Unlock className="w-3 h-3" /> Open Link <ExternalLink className="w-3 h-3 opacity-80" />
          </a>
        ) : setupSoon ? (
          <div className="mt-3 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded text-[11px] font-bold uppercase tracking-[0.18em] bg-foreground/10 text-foreground/60">
            Setting Up
          </div>
        ) : (
          <Link to="/signup" data-testid={`market-stream-locked-${program.id}`}
                className="mt-3 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded text-[11px] font-bold uppercase tracking-[0.18em] text-white" style={{ background: WINE }}>
            <Lock className="w-3 h-3" /> Upgrade To Unlock
          </Link>
        )}
      </div>
    </div>
  );
}
